import { useCallback, useEffect } from "react";
import { ethers } from "ethers";
import {
  wallet as walletSelector,
  setWallet,
  setStatus,
  setProvider,
  setNetwork,
  setTokenBalances,
  setSelectedToken,
  setPreferredChain,
} from "app/wallet/wallet";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { decimalAdjust } from "app/functions/numbers";
import { getNetworkByChainId } from "../config/networks";
import { getTokensForChain } from "../config/tokens";
import { trackWalletConnect, trackWalletDisconnect } from "../utils/analytics";

const STATUSES = {
  IDLE: "idle",
  LOADING: "loading",
  CONNECTED: "connected",
  DISCONNECTED: "disconnected",
  ERROR: "error",
  NOT_AVAILABLE: "provider_not_available",
};

const useWeb3 = () => {
  const wallet = useAppSelector(walletSelector);
  const dispatch = useAppDispatch();

  const { provider } = wallet;

  const connect = useCallback(
    (__provider) => {
      dispatch(setStatus(STATUSES.LOADING));
      // ethers v6: BrowserProvider instead of Web3Provider
      const Provider = new ethers.BrowserProvider(__provider);
      if (!Provider) {
        dispatch(setStatus(STATUSES.NOT_AVAILABLE));
      } else dispatch(setProvider(Provider));
    },
    [dispatch]
  );

  const accountsChanged = useCallback(
    (accounts) => {
      if (provider) {
        if (accounts.length === 0) {
          dispatch(setStatus(STATUSES.DISCONNECTED));
          trackWalletDisconnect();
        } else {
          provider
            .getNetwork()
            .then((network) => {
              const chainId = Number(network.chainId);
              const networkConfig = getNetworkByChainId(chainId);
              const nativeCurrency = networkConfig?.nativeCurrency?.symbol || "ETH";

              return provider.getBalance(accounts[0]).then((balance) => {
                dispatch(
                  setWallet({
                    provider: provider,
                    status: STATUSES.CONNECTED,
                    account: {
                      address: accounts[0],
                      balance: {
                        amount: decimalAdjust(
                          "ceil",
                          // ethers v6: formatUnits is directly on ethers
                          ethers.formatUnits(balance, 18),
                          -5
                        ),
                        currency: nativeCurrency,
                      },
                    },
                  })
                );
                // Track wallet connection
                trackWalletConnect("browser_wallet");
              });
            })
            .catch((error) => {
              console.log(error, error.message);
              dispatch(setStatus(STATUSES.DISCONNECTED));
            });
        }
      } else dispatch(setStatus(STATUSES.DISCONNECTED));
    },
    [dispatch, provider]
  );

  const getAccount = useCallback(() => {
    if (provider) {
      provider
        .send("eth_accounts", [])
        .then((accounts) => {
          if (accounts.length === 0) {
            provider
              .send("eth_requestAccounts", [])
              .then((_accounts) => accountsChanged(_accounts))
              .catch((error) => {
                console.log(error, error.message);
                dispatch(setStatus(STATUSES.DISCONNECTED));
              });
          } else accountsChanged(accounts);
        })
        .catch((error) => {
          console.log(error, error.message);
          dispatch(setStatus(STATUSES.DISCONNECTED));
        });
    } else dispatch(setStatus(STATUSES.DISCONNECTED));
  }, [provider, dispatch, accountsChanged]);

  const chainChanged = useCallback(() => {
    if (provider) {
      provider
        .getNetwork()
        .then((_network) =>
          dispatch(
            // ethers v6: chainId is a bigint
            setNetwork({ name: _network.name, chainId: Number(_network.chainId) })
          )
        )
        .catch((error) => {
          console.log(error, error.message);
          dispatch(setStatus(STATUSES.DISCONNECTED));
        });
    }
  }, [provider, dispatch]);

  const disconnect = useCallback(() => {
    dispatch(setStatus(STATUSES.DISCONNECTED));
  }, [dispatch]);

  const switchNetwork = useCallback(
    async (chainId) => {
      const network = getNetworkByChainId(chainId);
      if (!network) return;

      // Always use window.ethereum directly for network switching
      const ethereum = window.ethereum;
      if (!ethereum?.request) return;

      try {
        await ethereum.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: "0x" + chainId.toString(16) }],
        });
        // Update network state immediately after successful switch
        dispatch(setNetwork({ name: network.name, chainId: chainId }));
      } catch (switchError) {
        // Chain not added to wallet, try to add it
        if (switchError.code === 4902) {
          try {
            await ethereum.request({
              method: "wallet_addEthereumChain",
              params: [
                {
                  chainId: "0x" + chainId.toString(16),
                  chainName: network.name,
                  nativeCurrency: network.nativeCurrency,
                  rpcUrls: [network.rpcUrl],
                  blockExplorerUrls: [network.blockExplorer],
                },
              ],
            });
            // Update network state after adding chain
            dispatch(setNetwork({ name: network.name, chainId: chainId }));
          } catch (addError) {
            console.error("Failed to add network:", addError);
          }
        } else {
          console.error("Failed to switch network:", switchError);
        }
      }
    },
    [dispatch]
  );

  const fetchTokenBalances = useCallback(
    async (address, chainId) => {
      if (!address || !chainId) return;

      // Get fresh provider from wallet
      const ethereum = window.ethereum;
      if (!ethereum) return;

      const walletProvider = new ethers.BrowserProvider(ethereum);

      // Verify wallet is on the correct chain
      const network = await walletProvider.getNetwork();
      const walletChainId = Number(network.chainId);

      if (walletChainId !== chainId) {
        console.log(`Wallet on chain ${walletChainId}, need ${chainId}. Skipping balance fetch.`);
        return;
      }

      const tokens = getTokensForChain(chainId);
      const balances = {};
      const erc20Abi = ["function balanceOf(address) view returns (uint256)"];

      for (const [symbol, token] of Object.entries(tokens)) {
        try {
          if (token.address === "native") {
            const balance = await walletProvider.getBalance(address);
            balances[symbol] = ethers.formatUnits(balance, token.decimals);
          } else {
            const contract = new ethers.Contract(token.address, erc20Abi, walletProvider);
            const balance = await contract.balanceOf(address);
            balances[symbol] = ethers.formatUnits(balance, token.decimals);
          }
        } catch (e) {
          console.error("Failed to get balance for", symbol, e);
          balances[symbol] = "0";
        }
      }

      dispatch(setTokenBalances(balances));
    },
    [dispatch]
  );

  const selectToken = useCallback(
    (symbol) => {
      dispatch(setSelectedToken(symbol));
    },
    [dispatch]
  );

  const selectNetwork = useCallback(
    (chainId) => {
      dispatch(setPreferredChain(chainId));
    },
    [dispatch]
  );

  useEffect(() => {
    if (provider) {
      chainChanged();
      getAccount();
    }
  }, [provider, chainChanged, getAccount]);

  // Auto-switch disabled - we handle network switching manually when needed (e.g., in betButton, admin page)
  // This prevents duplicate switch requests that cause MetaMask to error

  useEffect(() => {
    // Always use window.ethereum directly for events (ethers v6 BrowserProvider doesn't support events)
    const ethereum = window.ethereum;
    if (!ethereum) return;

    const handleDisconnect = () => disconnect();
    const handleChainChanged = () => connect(ethereum);
    const handleAccountsChanged = (accounts) => accountsChanged(accounts);

    ethereum.on("disconnect", handleDisconnect);
    ethereum.on("chainChanged", handleChainChanged);
    ethereum.on("accountsChanged", handleAccountsChanged);

    return () => {
      ethereum.removeListener("disconnect", handleDisconnect);
      ethereum.removeListener("chainChanged", handleChainChanged);
      ethereum.removeListener("accountsChanged", handleAccountsChanged);
    };
  }, [accountsChanged, connect, disconnect]);

  return { connect, wallet, disconnect, switchNetwork, fetchTokenBalances, selectToken, selectNetwork };
};

export default useWeb3;
